// Quick-fire deck: swipe right to add to Want to try, left to pass
import React, { useMemo, useRef, useState } from 'react';
import { Heart, Play, RotateCcw, X } from 'lucide-react';
import { CATEGORIES } from '@/data';
import { getRedGifsThumbnail } from '@/lib/utils';
import type { Clip } from '@/types';
import { RedGifsImage } from '@/components/RedGifsImage';

const SWIPE_AT = 90;

interface SwipeDeckProps {
  clips: Clip[];                 // candidates (not tried, not already wanted)
  onWant: (clip: Clip) => void;
  onOpen: (clip: Clip) => void;
}

const shuffle = <T,>(list: T[]) => {
  const a = [...list];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j]!, a[i]!];
  }
  return a;
};

export const SwipeDeck: React.FC<SwipeDeckProps> = ({ clips, onWant, onOpen }) => {
  const [seed, setSeed] = useState(0);
  const deck = useMemo(() => shuffle(clips).slice(0, 12), [seed]); // eslint-disable-line react-hooks/exhaustive-deps
  const [index, setIndex] = useState(0);
  const [wanted, setWanted] = useState(0);
  const [dx, setDx] = useState(0);
  const [leaving, setLeaving] = useState<'left' | 'right' | null>(null);
  const start = useRef<{ x: number; moved: boolean } | null>(null);

  const current = deck[index];
  const next = deck[index + 1];

  const decide = (dir: 'left' | 'right') => {
    if (!current || leaving) return;
    setLeaving(dir);
    if (dir === 'right') { onWant(current); setWanted(w => w + 1); }
    setTimeout(() => { setIndex(i => i + 1); setDx(0); setLeaving(null); }, 220);
  };

  const onPointerDown = (e: React.PointerEvent) => {
    start.current = { x: e.clientX, moved: false };
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!start.current) return;
    const delta = e.clientX - start.current.x;
    if (Math.abs(delta) > 6) start.current.moved = true;
    setDx(delta);
  };
  const onPointerUp = () => {
    if (!start.current || !current) return;
    const { moved } = start.current;
    start.current = null;
    if (dx > SWIPE_AT) decide('right');
    else if (dx < -SWIPE_AT) decide('left');
    else { setDx(0); if (!moved) onOpen(current); }
  };

  const restart = () => { setSeed(s => s + 1); setIndex(0); setWanted(0); };

  const card = (clip: Clip, top: boolean) => {
    const thumb = getRedGifsThumbnail(clip.link);
    const cat = CATEGORIES.find(c => c.id === clip.categoryId);
    const x = top ? (leaving === 'right' ? 480 : leaving === 'left' ? -480 : dx) : 0;
    const tilt = top ? x / 18 : 0;
    const likeOpacity = top ? Math.max(0, Math.min(1, x / SWIPE_AT)) : 0;
    const passOpacity = top ? Math.max(0, Math.min(1, -x / SWIPE_AT)) : 0;
    return (
      <div
        key={clip.id}
        className={`deck-card absolute inset-0 rounded-[24px] overflow-hidden border border-line bg-surface select-none ${top ? 'cursor-grab active:cursor-grabbing' : 'scale-[0.94] translate-y-3 opacity-70'} ${top && start.current ? 'dragging' : ''}`}
        style={top ? { transform: `translateX(${x}px) rotate(${tilt}deg)`, opacity: leaving ? 0 : 1 } : undefined}
        onPointerDown={top ? onPointerDown : undefined}
        onPointerMove={top ? onPointerMove : undefined}
        onPointerUp={top ? onPointerUp : undefined}
        onPointerCancel={top ? () => { start.current = null; setDx(0); } : undefined}
      >
        {thumb ? (
          <RedGifsImage url={clip.link} alt="" draggable={false} className="absolute inset-0 w-full h-full object-cover pointer-events-none" />
        ) : (
          <span className="absolute inset-0 clip-thumb-fallback" />
        )}
        <span className="absolute inset-0 bg-gradient-to-t from-ground via-ground/20 to-transparent" />
        <span className="absolute left-4 top-4 h-9 px-3 rounded-xl border-2 border-action text-action font-extrabold tracking-wider text-[15px] flex items-center -rotate-12" style={{ opacity: likeOpacity }}>WANT</span>
        <span className="absolute right-4 top-4 h-9 px-3 rounded-xl border-2 border-ink-muted text-ink-2 font-extrabold tracking-wider text-[15px] flex items-center rotate-12" style={{ opacity: passOpacity }}>PASS</span>
        <span className="absolute inset-x-0 bottom-0 p-4">
          <span className="text-[12px] font-bold text-her">{cat?.emoji} {cat?.name}</span>
          <span className="block font-display text-[26px] leading-tight font-medium">{clip.title}</span>
          <span className="mt-1 flex items-center gap-1 text-[12px] text-ink-muted"><Play className="w-3 h-3 fill-current" /> Tap to watch</span>
        </span>
      </div>
    );
  };

  return (
    <section className="bg-surface/80 backdrop-blur border border-line rounded-[24px] p-4 space-y-3">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-[17px] font-bold">Quick fire</h2>
        <span className="text-[13px] text-ink-muted">{wanted > 0 ? `${wanted} added to Want to try` : 'Swipe right if it’s a yes'}</span>
      </div>

      {current ? (
        <>
          <div className="relative h-[360px] sm:h-[420px] w-full max-w-sm mx-auto">
            {next && card(next, false)}
            {card(current, true)}
          </div>
          <div className="flex items-center justify-center gap-5">
            <button onClick={() => decide('left')} aria-label="Pass" className="w-14 h-14 rounded-full bg-raised border border-line text-ink-2 hover:text-ink flex items-center justify-center">
              <X className="w-6 h-6" />
            </button>
            <span className="text-[12px] text-ink-muted w-14 text-center">{index + 1} / {deck.length}</span>
            <button onClick={() => decide('right')} aria-label="Want to try" className="w-14 h-14 rounded-full bg-her text-ground flex items-center justify-center hover:brightness-110">
              <Heart className="w-6 h-6 fill-current" />
            </button>
          </div>
        </>
      ) : (
        <div className="h-[200px] flex flex-col items-center justify-center text-center gap-2">
          <p className="font-display text-[24px]">{wanted > 0 ? `${wanted} new for your list` : 'That’s the lot'}</p>
          <button onClick={restart} disabled={clips.length === 0} className="h-11 px-4 rounded-xl bg-raised border border-line text-[14px] font-semibold flex items-center gap-2 disabled:opacity-50">
            <RotateCcw className="w-4 h-4" /> Deal another hand
          </button>
        </div>
      )}
    </section>
  );
};
